import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useWindowEvent } from '@elfsight/lib-utils';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import {
  GAP,
  MEDIUM_BREAKPOINT,
  MIN_COLUMN_WIDTH,
  SMALL_BREAKPOINT,
  SMALL_BREAKPOINT_MOBILE_COMPARISON
} from '../constants';
import { EmptyMessage } from '../main';
import { useTables } from '../tables-context';
import { getMaxRows } from '../utils';
import {
  defaultRowIndexes,
  type ExtendedLayouts,
  LayoutContext
} from './layout-context';
import { LayoutThemeProvider } from './layout-theme-provider';
import { layoutsMap } from './variants';

const MIN_RESIZE_DELTA = 20;
const DEFAULT_MARGINS = {
  left: 0,
  right: 0
};

export function Layout() {
  const { t } = useLingui();

  const { carouselArrowSize, layout, editMode, renderThumbnailMode } =
    useSettings();
  const { activeTable, elements } = useTables();
  const { columns = [] } = activeTable ?? {};
  const [internalLayout, setInternalLayout] = useState<ExtendedLayouts>(layout);

  const [checkoutErrorIndexes, setCheckoutErrorIndexes] = useState<Set<string>>(
    new Set()
  );

  useLayoutEffect(() => {
    setInternalLayout(layout);
  }, [layout]);

  const ref = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [width, setWidth] = useState(0);

  const rowIndexes = useMemo(() => {
    return elements.reduce((indexes, element, elementIndex) => {
      const rowIndex = elements.slice(0, elementIndex).reduce((sum, el) => {
        if (layout === 'grid' || layout === 'carousel') {
          return 0;
        }

        return (
          sum +
          getMaxRows({
            element: el,
            elements: elements,
            layout: internalLayout,
            table: activeTable
          })
        );
      }, 0);

      return {
        ...indexes,
        [element]: rowIndex
      };
    }, defaultRowIndexes);
  }, [elements, internalLayout, activeTable, layout]);

  const rowsCount = useMemo(() => {
    if (layout === 'grid' || layout === 'carousel') {
      return 0;
    }

    // Начинаем отсчет с 1 из-за блока с нижним паддингом
    return elements.reduce((sum, element) => {
      return (
        sum +
        getMaxRows({
          element,
          elements,
          layout: internalLayout,
          table: activeTable
        })
      );
    }, 1);
  }, [elements, internalLayout, activeTable, layout]);

  const previousSnapCarouselMargins = useRef(DEFAULT_MARGINS);
  const [snapCarouselMargins, setSnapCarouselMargins] =
    useState(DEFAULT_MARGINS);

  const overflows = useMemo(() => {
    const overflowWidth = MIN_COLUMN_WIDTH * columns.length;
    if (internalLayout === 'grid' || internalLayout === 'carousel') {
      const gapsWidth = (columns.length - 1) * GAP;

      return width < overflowWidth + gapsWidth;
    }

    return width < overflowWidth;
  }, [width, internalLayout, columns.length]);

  const isMediumBreakpoint = useMemo(() => {
    if (internalLayout === 'grid') {
      return overflows && width > SMALL_BREAKPOINT + GAP;
    }

    if (internalLayout === 'comparisonTableMobile') {
      return width < MEDIUM_BREAKPOINT + carouselArrowSize;
    }

    return !!width && width <= MEDIUM_BREAKPOINT;
  }, [width, internalLayout, overflows, carouselArrowSize]);

  const isSmallBreakpoint = useMemo(() => {
    if (internalLayout === 'grid' || internalLayout === 'carousel') {
      return width < SMALL_BREAKPOINT + GAP * 2 + 0.2 * MIN_COLUMN_WIDTH;
    }

    if (internalLayout === 'comparisonTableMobile') {
      return width < SMALL_BREAKPOINT_MOBILE_COMPARISON;
    }

    return width < SMALL_BREAKPOINT;
  }, [width, internalLayout]);

  const breakpoints = useMemo(
    () => ({
      overflows,
      isMediumBreakpoint,
      isSmallBreakpoint
    }),
    [overflows, isMediumBreakpoint, isSmallBreakpoint]
  );

  const handleResize = useCallback(() => {
    if (internalLayout === 'grid' || !ref.current) {
      return;
    }

    const { left: previousLeftMargin, right: previousRightMargin } =
      previousSnapCarouselMargins.current;
    const derivedPreviousLeftMargin = isMediumBreakpoint
      ? 0
      : previousLeftMargin;
    const derivedPreviousRightMargin = isMediumBreakpoint
      ? 0
      : previousRightMargin;

    const { left, right } = ref.current.getBoundingClientRect();
    const newLeft =
      left - carouselArrowSize / 2 - derivedPreviousLeftMargin < 0 &&
      internalLayout !== 'comparisonTable'
        ? carouselArrowSize / 2
        : 0;

    const newRight =
      right +
        derivedPreviousLeftMargin +
        derivedPreviousRightMargin +
        carouselArrowSize / 2 >
      document.documentElement.clientWidth
        ? carouselArrowSize / 2
        : 0;
    const margins = {
      left: newLeft,
      right: newRight
    };

    if (newLeft !== previousLeftMargin || newRight !== previousRightMargin) {
      previousSnapCarouselMargins.current = margins;
      setSnapCarouselMargins(margins);
    }
  }, [internalLayout, isMediumBreakpoint, carouselArrowSize]);
  useLayoutEffect(() => {
    if (!width) {
      return;
    }

    handleResize();
  }, [handleResize, width]);
  useWindowEvent('resize', handleResize);

  const refCallback = useCallback((node: HTMLDivElement) => {
    resizeObserverRef.current?.disconnect();
    ref.current = node;

    if (node) {
      const { offsetWidth: initialWidth } = node;
      setWidth(initialWidth);

      resizeObserverRef.current = new ResizeObserver(() => {
        const { offsetWidth: newWidth } = node;

        setWidth((previousWidth) => {
          if (Math.abs(previousWidth - newWidth) > MIN_RESIZE_DELTA) {
            return newWidth;
          }

          return previousWidth;
        });
      });
      resizeObserverRef.current.observe(node);
    }
  }, []);

  const checkIfCheckoutError = useCallback(
    (id: string) => {
      return checkoutErrorIndexes.has(id);
    },
    [checkoutErrorIndexes]
  );

  const setCheckoutError = useCallback((id: string) => {
    setCheckoutErrorIndexes((previousCheckoutErrorIndexes) => {
      const newCheckoutErrorIndexes = new Set(previousCheckoutErrorIndexes);
      newCheckoutErrorIndexes.add(id);

      return newCheckoutErrorIndexes;
    });
  }, []);

  useEffect(() => {
    setCheckoutErrorIndexes(new Set());
  }, [internalLayout, activeTable]);

  const context = useMemo(
    () => ({
      layout: internalLayout,
      setLayout: setInternalLayout,
      checkIfCheckoutError,
      setCheckoutError,
      breakpoints,
      snapCarouselMargins,
      rowIndexes,
      rowsCount
    }),
    [
      breakpoints,
      checkIfCheckoutError,
      setCheckoutError,
      snapCarouselMargins,
      internalLayout,
      rowIndexes,
      rowsCount
    ]
  );

  const LayoutComponent = layoutsMap[internalLayout];

  if (!columns.length && (editMode || renderThumbnailMode)) {
    return (
      <EmptyMessage>{t`Add at least 1 column to display the table`}</EmptyMessage>
    );
  }

  if (!elements.length) {
    return null;
  }

  return (
    <LayoutContext.Provider value={context}>
      <LayoutThemeProvider>
        <LayoutComponent ref={refCallback} />
      </LayoutThemeProvider>
    </LayoutContext.Provider>
  );
}
